import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AttendanceAPI } from "@/lib/sprint5-api";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, QrCode, LogIn } from "lucide-react";

export default function AttendanceQrCheckin() {
  const { token } = useParams();
  const { user } = useAuth();
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    setState("loading"); setError("");
    try {
      const r = await AttendanceAPI.qrCheckin(token);
      setResult(r);
      setState("done");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setState("error");
    }
  };

  // Auto-trigger for logged-in users
  useEffect(() => {
    if (user && state === "idle") submit();
  }, [user]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="qr-checkin-page">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 text-center">
        <div className="h-12 w-12 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <QrCode className="h-5 w-5" />
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-3xl">Attendance check-in</h1>

        {user === null && (
          <div className="mt-6"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        )}

        {user === false && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-muted-foreground">Sign in to mark your attendance.</p>
            <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`}>
              <Button data-testid="qr-signin"><LogIn className="h-3.5 w-3.5 mr-1.5" />Sign in</Button>
            </Link>
          </div>
        )}

        {user && state === "loading" && (
          <div className="mt-6"><Loader2 className="h-5 w-5 animate-spin inline" /><p className="text-sm text-muted-foreground mt-2">Checking you in…</p></div>
        )}

        {user && state === "done" && (
          <div className="mt-6 space-y-3" data-testid="qr-checkin-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
            <h2 className="font-display font-bold text-xl">You&apos;re checked in</h2>
            <p className="text-xs text-muted-foreground font-mono">{user.email}</p>
          </div>
        )}

        {user && state === "error" && (
          <div className="mt-6 space-y-3" data-testid="qr-checkin-error">
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="font-display font-bold text-xl">Check-in failed</h2>
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" onClick={submit} size="sm">Try again</Button>
          </div>
        )}
      </div>
    </div>
  );
}
